
/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * PreferenceSettingModal
 *
 * @file PreferenceSettingModal.jsx
 * @author Anuja Gaikwad
 * @since 1.0.27
  */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Dialog } from '@mui/material';
import DynamicToggle from '../common/DynamicToggle';
import Icons from '../icons/Icon';
import {
  PreferenceDialogPaper,
  PreferenceTitleRoot,
  PreferenceContentRoot,
  PreferenceActionsRoot,
  PreferenceTitle,
  PreferenceSubtitle,
  PreferenceSectionsWrapper,
  PreferenceSectionCard,
  PreferenceMainRow,
  PreferenceLabel,
  PreferenceSubLabel,
  PreferenceLearnMore,
  PreferenceCloseButton,
  PreferenceFooter,
  PreferenceConfirmButton,
  PreferenceStudyPrepDivider
} from './styles/PreferenceSettingStyles';

const PreferenceSettingModal = (props) => {
  const {
    open,
    onClose,
    onConfirm,
    preferenceSettings,
    onToggleChange
  } = props;

  const [toggles, setToggles] = useState({});

  useEffect(() => {
    if (!preferenceSettings?.sections) return;

    const initialToggles = preferenceSettings.sections.reduce((acc, { key, value }) => {
      acc[key] = value;

      return acc;
    }, {});

    setToggles(initialToggles);
  }, [preferenceSettings]);

  const handleToggleChange = (key) => {
    setToggles(prev => ({ ...prev, [key]: !prev[key] }));
    onToggleChange(key, !toggles[key]);
  };

  const getInitialValue = (key) => {
    const section = preferenceSettings?.sections?.find(item => item.key === key);

    return section?.value;
  };

  const hasChanged = () => Object.keys(toggles).some(key => toggles[key] !== getInitialValue(key));

  const handleConfirm = () => {
    const changedToggles = Object.keys(toggles).reduce((acc, key) => {
      if (toggles[key] !== getInitialValue(key)) {
        acc[key] = toggles[key];
      }

      return acc;
    }, {});

    onConfirm(changedToggles);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperComponent={PreferenceDialogPaper}
    >
      <PreferenceCloseButton
        aria-label="Close"
        onClick={onClose}
      >
        <Icons name="CloseIcon" />
      </PreferenceCloseButton>
      <PreferenceTitleRoot>
        <PreferenceTitle>{preferenceSettings.title}</PreferenceTitle>
        <PreferenceSubtitle>{preferenceSettings.subTitle}</PreferenceSubtitle>
      </PreferenceTitleRoot>
      <PreferenceContentRoot>
        <PreferenceSectionsWrapper>
          {preferenceSettings.sections.map((item) => (
            <PreferenceSectionCard key={item.key}>
              <PreferenceMainRow>
                <PreferenceLabel>{item.label}</PreferenceLabel>
                <DynamicToggle
                  label=""
                  value={toggles[item?.key] ?? false}
                  onChange={() => handleToggleChange(item.key)}
                />
              </PreferenceMainRow>

              {item.subLabel && (
                <PreferenceSubLabel>
                  <span>{item.subLabel}</span>
                </PreferenceSubLabel>
              )}

              {item.showDivider && <PreferenceStudyPrepDivider />}

              {item.descriptionText?.length > 0 && (
              <PreferenceSubLabel>
                {item.descriptionText?.map((desc, idx) => (
                  <div
                    key={idx}
                    dangerouslySetInnerHTML={{ __html: desc }}
                  />
                ))}
              </PreferenceSubLabel>
              )}
              {item.helpLinkText && item.onHelpLinkClick && (
                <PreferenceLearnMore
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    item.onHelpLinkClick();
                  }}
                >
                  {item.helpLinkText}
                </PreferenceLearnMore>
              )}
            </PreferenceSectionCard>
          ))}
        </PreferenceSectionsWrapper>
      </PreferenceContentRoot>
      <PreferenceActionsRoot>
        <PreferenceFooter>
          <PreferenceConfirmButton
            type="button"
            onClick={handleConfirm}
            disabled={!hasChanged()}
          >
            {preferenceSettings.ctaLabel}
          </PreferenceConfirmButton>
        </PreferenceFooter>
      </PreferenceActionsRoot>
    </Dialog>
  );
};

PreferenceSettingModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  onToggleChange: PropTypes.func,
  preferenceSettings: PropTypes.shape({
    title: PropTypes.string,
    subTitle: PropTypes.string,
    ctaLabel: PropTypes.string,
    sections: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        subLabel: PropTypes.string,
        value: PropTypes.bool.isRequired,
        descriptionText: PropTypes.arrayOf(PropTypes.string),
        showDivider: PropTypes.bool,
        helpLinkText: PropTypes.string
      })
    ).isRequired
  }).isRequired
};
PreferenceSettingModal.defaultProps = {
  open: false,
  onClose: () => {},
  onConfirm: null,
  onToggleChange: () => {}
};
export default PreferenceSettingModal;
